import { ScaleElement } from '@scale/base-element';
import { Events } from '@scale/events';

class ProductMediaGallery extends ScaleElement {
  #currentIndex = 0;
  #slides = [];
  #thumbs = [];
  #indicators = [];
  #variantMediaMap = null;
  #allSlides = [];
  #allThumbs = [];
  #allIndicators = [];
  #allGridItems = [];
  #slideObserver = null;
  #isScrollSnap = true;
  #scrollTimeout = null;
  #zoomFrame = 0;

  setup() {
    this.#allSlides = [...this.querySelectorAll('.product-media-gallery__slide')];
    this.#allThumbs = [...this.querySelectorAll('.product-media-gallery__thumb')];
    this.#allIndicators = [...this.querySelectorAll('.product-media-gallery__indicator')];
    this.#allGridItems = [...this.querySelectorAll('.product-media-gallery__item')];
    this.#collectVisible();

    const transition = this.dataset.transition || 'slide';
    this.#isScrollSnap = transition === 'slide' || transition === 'none';

    if (!this.#isScrollSnap) {
      this.classList.add(`product-media-gallery--${transition}`);
      if (this.#slides[0]) this.#slides[0].classList.add('is-active');
    }

    if (this.hasAttribute('data-hide-variant-images')) {
      this.#loadVariantMediaMap();
    }

    this.#setupZoom();
    this.#setupLightbox();
    this.#setupVariantSync();

    if (this.#allSlides.length <= 1) return;

    this.#setupScrollTracking();
    this.#setupArrows();
    this.#setupThumbnails();
    this.#setupIndicators();
    this.#setupKeyboard();
    this.#setupAdaptiveHeight();
    this.#syncUI();

    if (Shopify?.designMode) {
      this.onDocument('shopify:block:select', (e) => {
        if (e.detail.blockId === this.closest('[id]')?.id?.replace('shopify-block-', '')) {
          this.#goTo(0);
        }
      });
    }
  }

  teardown() {
    this.#slideObserver?.disconnect();
    this.#slideObserver = null;
    cancelAnimationFrame(this.#zoomFrame);
    this.#zoomFrame = 0;
  }


  #setupScrollTracking() {
    if (!this.#isScrollSnap) return;

    const track = this.ref('track');
    if (!track) return;

    this.#slideObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const index = this.#slides.indexOf(entry.target);
            if (index >= 0 && index !== this.#currentIndex) {
              this.#currentIndex = index;
              this.#syncUI();
            }
          }
        }
      },
      { root: track, threshold: 0.5 }
    );

    for (const slide of this.#slides) {
      this.#slideObserver.observe(slide);
    }

    this.on('scroll', () => {
      clearTimeout(this.#scrollTimeout);
      this.#scrollTimeout = setTimeout(() => this.#updateArrows(), 100);
    }, { target: track, passive: true });
  }


  #setupArrows() {
    const prev = this.ref('prev');
    const next = this.ref('next');
    if (prev) this.on('click', () => this.#goTo(this.#currentIndex - 1), { target: prev });
    if (next) this.on('click', () => this.#goTo(this.#currentIndex + 1), { target: next });
  }

  #goTo(index, behavior = 'smooth') {
    if (index < 0 || index >= this.#slides.length) return;

    if (this.#isScrollSnap) {
      const slide = this.#slides[index];
      if (slide) slide.scrollIntoView({ behavior, block: 'nearest', inline: 'start' });
    } else {
      this.#slides[this.#currentIndex]?.classList.remove('is-active');
      this.#slides[index]?.classList.add('is-active');
    }

    this.#currentIndex = index;
    this.#syncUI();
  }

  #goToMedia(mediaId) {
    const id = String(mediaId);
    const index = this.#slides.findIndex((s) => s.dataset.mediaId === id);
    if (index >= 0) this.#goTo(index);
  }


  #syncUI() {
    this.#updateAmbient();
    this.#updateThumbs();
    this.#updateIndicators();
    this.#updateArrows();
    this.#handleMediaPlayback();
    this.#updateTrackHeight();

    const label = `${this.#currentIndex + 1} / ${this.#slides.length}`;
    const counter = this.ref('counter');
    if (counter) counter.textContent = label;
    const liveRegion = this.ref('live-region');
    if (liveRegion) liveRegion.textContent = label;

    for (let i = 0; i < this.#slides.length; i++) {
      this.#slides[i].setAttribute('aria-hidden', String(i !== this.#currentIndex));
      this.#slides[i].setAttribute('aria-label', `${i + 1} / ${this.#slides.length}`);
      const open = this.#slides[i].querySelector('[data-lightbox-open]');
      if (open) open.tabIndex = i === this.#currentIndex ? 0 : -1;
    }
  }

  #updateAmbient() {
    const layer = this.ref('ambient');
    const image = this.#slides[this.#currentIndex]?.querySelector('img');
    if (!layer || !image) return;

    const src = image.currentSrc;
    if (!src) {
      image.addEventListener('load', () => this.#updateAmbient(), { once: true });
      return;
    }
    if (layer.dataset.src === src) return;

    layer.dataset.src = src;
    const [first, second] = layer.children;
    const next = first.classList.contains('is-on') ? second : first;
    next.style.backgroundImage = `url("${src}")`;
    next.classList.add('is-on');
    (next === first ? second : first).classList.remove('is-on');
  }


  #setupAdaptiveHeight() {
    const firstMedia = this.#slides[0]?.querySelector('.product-media');
    if (!firstMedia?.classList.contains('product-media--adapt')) return;

    for (const slide of this.#allSlides) {
      const img = slide.querySelector('img');
      if (img && !img.complete) {
        img.addEventListener('load', () => this.#updateTrackHeight(), { once: true });
      }
    }

    this.on('resize', () => this.debounce('track-height', () => this.#updateTrackHeight(), 100), { target: window, passive: true });
  }

  #updateTrackHeight() {
    const track = this.ref('track');
    if (!track) return;

    if (this.dataset.mobileLayout === 'stack' && window.matchMedia('(width < 750px)').matches) {
      track.style.height = '';
      return;
    }

    const firstMedia = this.#slides[0]?.querySelector('.product-media');
    if (!firstMedia?.classList.contains('product-media--adapt')) return;

    const active = this.#slides[this.#currentIndex];
    if (!active) return;

    const height = active.getBoundingClientRect().height;
    if (height > 0) track.style.height = `${height}px`;
  }


  #handleMediaPlayback() {
    for (let i = 0; i < this.#slides.length; i++) {
      const video = this.#slides[i].querySelector('video');
      if (!video) continue;
      if (i === this.#currentIndex) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    }
  }

  #updateThumbs() {
    for (const thumb of this.#allThumbs) {
      thumb.classList.remove('is-active');
      thumb.removeAttribute('aria-current');
    }
    for (let i = 0; i < this.#thumbs.length; i++) {
      const isActive = i === this.#currentIndex;
      this.#thumbs[i].classList.toggle('is-active', isActive);
      if (isActive) this.#thumbs[i].setAttribute('aria-current', 'true');
    }
    this.#scrollActiveThumbIntoView();
  }

  #scrollActiveThumbIntoView() {
    const active = this.#thumbs[this.#currentIndex];
    const container = this.ref('thumbnails');
    if (!active || !container) return;

    const isVertical = container.scrollHeight > container.clientHeight;
    const isHorizontal = container.scrollWidth > container.clientWidth;
    if (!isVertical && !isHorizontal) return;

    const thumbRect = active.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    if (isVertical) {
      const offset = thumbRect.top - containerRect.top + container.scrollTop;
      const target = offset - (container.clientHeight - thumbRect.height) / 2;
      container.scrollTo({ top: target, behavior: 'smooth' });
    } else {
      const offset = thumbRect.left - containerRect.left + container.scrollLeft;
      const target = offset - (container.clientWidth - thumbRect.width) / 2;
      container.scrollTo({ left: target, behavior: 'smooth' });
    }
  }

  #updateIndicators() {
    for (const indicator of this.#allIndicators) {
      indicator.classList.remove('is-active');
      indicator.removeAttribute('aria-current');
    }
    for (let i = 0; i < this.#indicators.length; i++) {
      const isActive = i === this.#currentIndex;
      this.#indicators[i].classList.toggle('is-active', isActive);
      if (isActive) this.#indicators[i].setAttribute('aria-current', 'true');
    }
  }

  #updateArrows() {
    const prev = this.ref('prev');
    const next = this.ref('next');
    if (prev) prev.disabled = this.#currentIndex === 0;
    if (next) next.disabled = this.#currentIndex === this.#slides.length - 1;
  }


  #setupThumbnails() {
    for (const thumb of this.#allThumbs) {
      this.on('click', () => {
        const visibleIndex = this.#slides.findIndex(
          (s) => s.dataset.mediaId === thumb.dataset.mediaId
        );
        if (visibleIndex >= 0) this.#goTo(visibleIndex);
      }, { target: thumb });
    }

    this.#setupThumbsArrows();
  }

  #setupThumbsArrows() {
    const container = this.ref('thumbnails');
    const prev = this.ref('thumbs-prev');
    const next = this.ref('thumbs-next');
    if (!container || !prev || !next || this.#allThumbs.length <= 1) return;

    const scrollAmount = 200;

    this.on('click', () => {
      const isVertical = container.scrollHeight > container.clientHeight;
      container.scrollBy({ left: isVertical ? 0 : -scrollAmount, top: isVertical ? -scrollAmount : 0, behavior: 'smooth' });
    }, { target: prev });

    this.on('click', () => {
      const isVertical = container.scrollHeight > container.clientHeight;
      container.scrollBy({ left: isVertical ? 0 : scrollAmount, top: isVertical ? scrollAmount : 0, behavior: 'smooth' });
    }, { target: next });

    this.on('scroll', () => this.debounce('thumbs-scroll', () => this.#updateThumbsArrows(), 50), { target: container, passive: true });
    this.#updateThumbsArrows();
  }

  #updateThumbsArrows() {
    const container = this.ref('thumbnails');
    const prev = this.ref('thumbs-prev');
    const next = this.ref('thumbs-next');
    if (!container || !prev || !next) return;

    const isHorizontal = container.scrollWidth > container.clientWidth;
    const isVertical = container.scrollHeight > container.clientHeight;

    if (isHorizontal) {
      prev.hidden = container.scrollLeft <= 1;
      next.hidden = container.scrollLeft + container.clientWidth >= container.scrollWidth - 1;
    } else if (isVertical) {
      prev.hidden = container.scrollTop <= 1;
      next.hidden = container.scrollTop + container.clientHeight >= container.scrollHeight - 1;
    } else {
      prev.hidden = true;
      next.hidden = true;
    }
  }


  #setupIndicators() {
    for (const indicator of this.#allIndicators) {
      this.on('click', () => {
        const allIndex = Number(indicator.dataset.index);
        const slide = this.#allSlides[allIndex];
        const visibleIndex = this.#slides.indexOf(slide);
        if (visibleIndex >= 0) this.#goTo(visibleIndex);
      }, { target: indicator });
    }
  }


  #setupKeyboard() {
    this.on('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.#goTo(this.#currentIndex - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.#goTo(this.#currentIndex + 1); }
    });
    if (!this.getAttribute('tabindex')) this.setAttribute('tabindex', '0');
  }


  #setupVariantSync() {
    this.onDocument(Events.VARIANT_SELECTED, (e) => {
      if (!e.variant) return;
      if (this.#variantMediaMap) this.#filterMediaForVariant(e.variant);
      if (e.variant.featured_media?.id) this.#goToMedia(e.variant.featured_media.id);
    });

    this.onDocument('scale:media:preview', (e) => {
      if (e.detail?.mediaId) this.#goToMedia(e.detail.mediaId);
    });
  }


  #setupZoom() {
    for (const trigger of this.querySelectorAll('[data-zoom-trigger]')) {
      this.on('click', (e) => this.#toggleZoom(trigger, e), { target: trigger });
      this.on('pointermove', (e) => this.#followZoom(trigger, e), { target: trigger });
      this.on('pointerleave', (e) => {
        if (e.pointerType === 'mouse') this.#zoomOut(trigger);
      }, { target: trigger });
    }
  }

  #toggleZoom(trigger, e) {
    if (trigger.classList.contains('is-zoomed')) {
      this.#zoomOut(trigger);
      return;
    }
    this.#aimZoom(trigger, e);
    trigger.classList.add('is-zoomed');
    this.#loadZoomSource(trigger);
  }

  #loadZoomSource(trigger) {
    const image = trigger.querySelector('.product-media__image');
    const sizes = `${Math.ceil(trigger.offsetWidth * 2)}px`;
    if (image.sizes === sizes) return;
    const srcset = trigger.dataset.zoomSrcset ? `${image.srcset}, ${trigger.dataset.zoomSrcset}` : image.srcset;
    const source = new Image();
    source.addEventListener('load', () => {
      image.srcset = srcset;
      image.sizes = sizes;
      delete trigger.dataset.zoomSrcset;
    }, { once: true });
    source.sizes = sizes;
    source.srcset = srcset;
  }

  #followZoom(trigger, e) {
    if (e.pointerType !== 'mouse' || !trigger.classList.contains('is-zoomed')) return;
    cancelAnimationFrame(this.#zoomFrame);
    this.#zoomFrame = requestAnimationFrame(() => {
      this.#zoomFrame = 0;
      this.#aimZoom(trigger, e);
    });
  }

  #zoomOut(trigger) {
    cancelAnimationFrame(this.#zoomFrame);
    this.#zoomFrame = 0;
    trigger.classList.remove('is-zoomed');
  }

  #aimZoom(trigger, e) {
    const rect = trigger.getBoundingClientRect();
    trigger.style.setProperty('--zoom-x', `${((e.clientX - rect.left) / rect.width) * 100}%`);
    trigger.style.setProperty('--zoom-y', `${((e.clientY - rect.top) / rect.height) * 100}%`);
  }


  #setupLightbox() {
    const lightbox = this.querySelector('product-lightbox');
    if (!lightbox) return;
    const load = () => import(lightbox.dataset.module);

    for (const trigger of this.querySelectorAll('[data-lightbox-open]')) {
      this.on('pointerenter', load, { target: trigger });
      this.on('click', async () => {
        await load();
        const triggers = [...this.querySelectorAll('[data-lightbox-open]')].filter((element) => !element.closest('.is-hidden'));
        lightbox.openFrom(triggers, trigger, (shown) => this.#reveal(shown));
      }, { target: trigger });
    }
  }

  #reveal(trigger) {
    const index = this.#slides.indexOf(trigger.closest('.product-media-gallery__slide'));
    if (index >= 0 && index !== this.#currentIndex) this.#goTo(index, 'instant');
    trigger.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'nearest' });
    return trigger;
  }


  #loadVariantMediaMap() {
    const json = this.ref('variant-media-json');
    if (!json) return;
    try {
      this.#variantMediaMap = JSON.parse(json.textContent);
    } catch {   }
  }

  #filterMediaForVariant(variant) {
    const selectedMediaId = variant.featured_media?.id ? String(variant.featured_media.id) : null;
    const hiddenMediaIds = new Set(
      Object.values(this.#variantMediaMap).map(String).filter((id) => id !== selectedMediaId)
    );

    for (const media of [...this.#allSlides, ...this.#allThumbs, ...this.#allGridItems]) {
      media.classList.toggle('is-hidden', hiddenMediaIds.has(media.dataset.mediaId));
    }
    for (const indicator of this.#allIndicators) {
      const slide = this.#allSlides[Number(indicator.dataset.index)];
      indicator.classList.toggle('is-hidden', slide.classList.contains('is-hidden'));
    }

    const media = this.#allSlides.length ? this.#allSlides : this.#allGridItems;
    const visibleCount = media.filter((item) => !item.classList.contains('is-hidden')).length;
    this.classList.toggle('product-media-gallery--single', visibleCount <= 1);

    this.#rebuildVisibleIndexes();
  }

  #collectVisible() {
    const isVisible = (element) => !element.classList.contains('is-hidden');
    this.#slides = this.#allSlides.filter(isVisible);
    this.#thumbs = this.#allThumbs.filter(isVisible);
    this.#indicators = this.#allIndicators.filter(isVisible);
  }

  #rebuildVisibleIndexes() {
    const current = this.#slides[this.#currentIndex];
    this.#collectVisible();

    if (this.#slideObserver) {
      this.#slideObserver.disconnect();
      for (const slide of this.#slides) this.#slideObserver.observe(slide);
    }
    this.#updateThumbsArrows();

    const index = this.#slides.indexOf(current);
    if (index >= 0) {
      this.#currentIndex = index;
      this.#syncUI();
      return;
    }
    current?.classList.remove('is-active');
    this.#currentIndex = 0;
    this.#goTo(0);
  }
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}
